import { omit } from 'lodash'
import { sourceTypes, selectors } from '@adalo/constants'

import { BindingSource, Form, IBinding } from 'utils/responsiveTypes'

import { Field } from './TableTypes'

type SimpleField = {
  name: string
  type: string
}

type BindingInput = {
  fieldId: string
  label: string
  type: string
  name?: string
  listObjectId: string | undefined
  source: BindingSource | undefined
}

export const parseTableFields = (
  fields: Record<string, SimpleField> | undefined
): Form[] => {
  if (!fields) {
    return []
  }

  const newFields = []

  for (const fieldId in fields) {
    if (fieldId) {
      newFields.push({
        fieldId,
        label: fields[fieldId]?.name ?? '',
        type: fields[fieldId]?.type ?? '',
      })
    }
  }

  return newFields
}

export const generateBindingSource = (input: BindingInput) => {
  const source = {
    type: sourceTypes.FIELD,
    label: input?.name ?? input.label,
    selector: {
      type: selectors.LIST_ITEM,
      listObjectId: input.listObjectId,
    },
    source: {
      ...input.source,
      dataType: input.type,
      fieldId: input.fieldId,
    },
  }

  return {
    type: 'LIBRARY_PROP',
    source,
  }
}

export const updateBindingType = (
  field: Field,
  option: IBinding['format'],
  onChange: (field: Field, index: number) => void,
  fieldIndex: number
): void => {
  if (!field.binding) {
    console.error('binding not found for field', field.label)

    return
  }

  const updatedField: Field = {
    ...field,
    binding: {
      ...field.binding,
      format: {
        ...field.binding.format,
        ...option,
      },
    },
  }

  onChange(updatedField, fieldIndex)
}

export const updateBindingRequiredText = (
  field: Field,
  value: boolean,
  onChange: (field: Field, index: number) => void,
  fieldIndex: number
) => {
  if (!field.binding) {
    console.error('binding not found for field', field.label)

    return
  }

  const updatedField: Field = {
    ...field,
    binding: {
      ...field.binding,
      format: {
        ...field.binding?.format,
        requiredText: value ? 'This field is required' : '',
        required: value,
      },
    },
  }

  onChange(updatedField, fieldIndex)
}

export const updateBindingFirstColumn = (
  field: Field,
  value: boolean,
  onChange: (field: Field, index: number) => void,
  fieldIndex: number
) => {
  if (!field.binding) {
    console.error('binding not found for field', field.label)

    return
  }

  const updatedField: Field = {
    ...field,
    binding: {
      ...field.binding,
      format: {
        ...field.binding?.format,
        shouldFreezeFirstColumn: value,
      },
    },
  }

  onChange(updatedField, fieldIndex)
}

export const updateBindingPrefixSuffix = (
  field: Field,
  suffix: Field['binding'],
  onChange: (field: Field, index: number) => void,
  fieldIndex: number
) => {
  if (!field.binding) {
    console.error('binding not found for field', field.label)

    return
  }

  if (!suffix) {
    console.error('suffix was not provided')

    return
  }

  const { format } = suffix

  const { ...prefixProps } = omit(format, 'type')

  const updatedField: Field = {
    ...field,
    binding: {
      ...field.binding,
      format: {
        ...field.binding?.format,
        ...prefixProps,
      },
    },
  }

  onChange(updatedField, fieldIndex)
}

export const updateLabel = (
  field: Field,
  currentField: Field,
  onChange: (field: Field, index: number) => void,
  fieldIndex: number
) => {
  onChange(currentField, fieldIndex)
}
